import { type ApprovedExpenseItemConstructor } from '@webapp/modules/expense/entities/ApprovedExpenseItem'
import { type ExpenseStatusEnum } from '@webapp/modules/expense/entities/enums/ExpenseStatusEnum'
import { type ExpenseTypeEnum } from '@webapp/modules/expense/entities/enums/ExpenseTypeEnum'
import {
  type AccountCodeTypeEnum,
  type AccountConstructor,
} from '@webapp/platform/finance'
import { mapExpenseTypeFieldsToLabel } from './mapApprovedExpenseItemApiModel'
import {
  type ExpenseReportItemAccountApiModel,
  type ExpenseReportItemsApiModel,
} from '../api-models/ExpenseReportItemsApiModel'

const mapExpenseReportItemAccountApiModel = (
  account: ExpenseReportItemAccountApiModel
): AccountConstructor => ({
  id: account.id,
  active: account.active,
  accountCode: {
    id: account.account_code.id,
    code: account.account_code.code,
    description: account.account_code.description,
    type: account.account_code.account_type as AccountCodeTypeEnum,
  },
  // v1 report items carry no department — the correction form scopes account
  // options by its own SecondaryCategory field
  secondaryCategory: null,
})

/**
 * Maps the v1 report of GET /api/expense/{reportId}/ to summary constructors.
 * Report items carry no per-item user/localCurrency/notes/approved_datetime —
 * requester and currencyId are sourced from the report, notes default to
 * null, and lineNumber is provisionally assigned by index (the correction
 * use case recomputes it after excluding the opened item).
 */
export const mapExpenseReportItemsApiModel = (
  report: ExpenseReportItemsApiModel
): ApprovedExpenseItemConstructor[] => {
  return report.items.map((item, index) => ({
    id: item.id,
    lineNumber: index + 1,
    merchant: item.merchant,
    amount: item.amount,
    currencyId: report.currency.id,
    expenseType: item.expense_type as ExpenseTypeEnum,
    expenseTypeLabel: mapExpenseTypeFieldsToLabel(item.expense_type_fields),
    attachment: item.attachment ?? null,
    notes: null,
    approvedDate: null,
    requester: {
      id: report.user.id,
      firstName: report.user.firstName,
      lastName: report.user.lastName,
    },
    expenseReport: {
      id: report.id,
      uuid: report.uuid,
      status: report.status as ExpenseStatusEnum,
      createdDate: report.createdDate,
      hasSiblingItems: report.items.length > 1,
      notes: report.notes ?? null,
    },
    account: mapExpenseReportItemAccountApiModel(item.account),
  }))
}
